import React, { useState, useEffect } from 'react';
import { useTelegram } from './TelegramProvider';
import { useTon } from './TelegramTonProvider';

export const TonPurchaseModal = ({ isOpen, onClose }) => {
    const [tonAmount, setTonAmount] = useState('1');
    const [lilapeAmount, setLilapeAmount] = useState(100000);
    const [isProcessing, setIsProcessing] = useState(false);
    const [purchaseStep, setPurchaseStep] = useState('input');
    const [transactionHash, setTransactionHash] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const { showAlert, hapticFeedback } = useTelegram();
    const { connected, wallet, sendTransaction } = useTon();

    // Stage 1: 1 TON = 5 Billion LILAPE
    const EXCHANGE_RATE = parseInt(process.env.REACT_APP_EXCHANGE_RATE) || 5000000000;
    const MIN_PURCHASE = parseFloat(process.env.REACT_APP_MIN_PURCHASE) || 0.1;
    const RECEIVER_ADDRESS = process.env.REACT_APP_TON_CONTRACT_ADDRESS || 'UQC3Xk4lBHUOCjOYf4IInNPko4WBWu5KHxZCo7qZSuWH7a2v';

    useEffect(() => {
        const amount = parseFloat(tonAmount) || 0;
        setLilapeAmount(Math.floor(amount * EXCHANGE_RATE));
    }, [tonAmount, EXCHANGE_RATE]);

    const handlePurchase = async () => {
        if (!connected || !wallet) {
            showAlert?.('Connect TON wallet first');
            return;
        }

        const amount = parseFloat(tonAmount);
        if (amount < MIN_PURCHASE) {
            showAlert?.(`Minimum: ${MIN_PURCHASE} TON`);
            return;
        }

        setIsProcessing(true);
        setPurchaseStep('sending');
        hapticFeedback?.('light');

        try {
            const transaction = {
                validUntil: Math.floor(Date.now() / 1000) + 300,
                messages: [{
                    address: RECEIVER_ADDRESS,
                    amount: (amount * 1e9).toString(),
                    payload: ''
                }]
            };

            const result = await sendTransaction(transaction);

            if (result?.boc) {
                setTransactionHash(result.boc);
                setPurchaseStep('success');
                setLilapeAmount(amount * EXCHANGE_RATE);

                if (window.Telegram?.WebApp) {
                    window.Telegram.WebApp.showAlert(
                        `🎉 Success! You'll receive ${(amount * EXCHANGE_RATE).toLocaleString()} LILAPE`
                    );
                }
            } else {
                throw new Error('Transaction not sent');
            }
        } catch (error) {
            console.error('Purchase error:', error);
            setPurchaseStep('error');
            setErrorMessage(error.message || 'Transaction failed');
            hapticFeedback?.('heavy');
        } finally {
            setIsProcessing(false);
        }
    };

    const resetModal = () => {
        setPurchaseStep('input');
        setTransactionHash('');
        setErrorMessage('');
        setTonAmount('1');
        setIsProcessing(false);
    };

    const handleClose = () => {
        resetModal();
        onClose();
    };

    if (!isOpen) return null;

    const styles = {
        overlay: {
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
        },
        modal: {
            background: '#1a1a2e',
            borderRadius: 20,
            padding: 24,
            width: '100%',
            maxWidth: 380,
            border: '1px solid rgba(255,255,255,0.1)',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
        },
        title: {
            fontSize: 20,
            fontWeight: 700,
            color: '#fff',
        },
        closeBtn: {
            background: 'none',
            border: 'none',
            color: '#888',
            fontSize: 28,
            cursor: 'pointer',
            padding: 0,
            lineHeight: 1,
        },
        label: {
            display: 'block',
            color: '#aaa',
            fontSize: 13,
            marginBottom: 8,
        },
        input: {
            width: '100%',
            background: '#0f0f1a',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 12,
            padding: '14px 16px',
            fontSize: 18,
            fontFamily: 'monospace',
            outline: 'none',
        },
        hint: {
            color: '#666',
            fontSize: 11,
            marginTop: 6,
        },
        infoBox: {
            background: '#0f0f1a',
            borderRadius: 14,
            padding: 16,
            marginTop: 20,
            marginBottom: 20,
        },
        infoRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 0',
        },
        infoLabel: {
            color: '#888',
            fontSize: 13,
        },
        infoValue: {
            color: '#fff',
            fontSize: 13,
            fontFamily: 'monospace',
        },
        infoValueGold: {
            color: '#F59E0B',
            fontSize: 18,
            fontWeight: 700,
        },
        buyBtn: {
            width: '100%',
            background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: 14,
            padding: '16px 24px',
            fontSize: 16,
            fontWeight: 700,
            cursor: 'pointer',
            opacity: (!connected || isProcessing || parseFloat(tonAmount) < MIN_PURCHASE) ? 0.5 : 1,
        },
        centerBox: {
            textAlign: 'center',
            padding: '40px 20px',
        },
        spinner: {
            width: 48,
            height: 48,
            border: '4px solid rgba(59,130,246,0.2)',
            borderTop: '4px solid #3B82F6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px',
        },
        successIcon: {
            width: 60,
            height: 60,
            background: '#10B981',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
            margin: '0 auto 20px',
        },
        errorIcon: {
            width: 60,
            height: 60,
            background: '#EF4444',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
            margin: '0 auto 20px',
        },
        statusTitle: {
            fontSize: 18,
            fontWeight: 700,
            color: '#fff',
            marginBottom: 8,
        },
        statusText: {
            fontSize: 14,
            color: '#888',
        },
        successAmount: {
            fontSize: 24,
            fontWeight: 900,
            color: '#10B981',
            margin: '16px 0',
        },
        errorText: {
            fontSize: 14,
            color: '#EF4444',
            marginBottom: 20,
        },
        btnRow: {
            display: 'flex',
            gap: 12,
        },
        btnSecondary: {
            flex: 1,
            background: '#374151',
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            padding: '14px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
        },
        btnSuccess: {
            width: '100%',
            background: '#10B981',
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            padding: '14px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
        },
        btnError: {
            flex: 1,
            background: '#EF4444',
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            padding: '14px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
        },
    };

    return (
        <div style={styles.overlay} onClick={handleClose}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
                <div style={styles.header}>
                    <h2 style={styles.title}>Buy LILAPE</h2>
                    <button style={styles.closeBtn} onClick={handleClose}>×</button>
                </div>

                {purchaseStep === 'input' && (
                    <>
                        <div>
                            <label style={styles.label}>Amount in TON</label>
                            <input
                                type="number"
                                value={tonAmount}
                                onChange={(e) => setTonAmount(e.target.value)}
                                min={MIN_PURCHASE}
                                step="0.1"
                                style={styles.input}
                                placeholder="1.0"
                            />
                            <p style={styles.hint}>Minimum: {MIN_PURCHASE} TON</p>
                        </div>

                        <div style={styles.infoBox}>
                            <div style={styles.infoRow}>
                                <span style={styles.infoLabel}>Exchange rate:</span>
                                <span style={styles.infoValue}>1 TON = {EXCHANGE_RATE.toLocaleString()} LILAPE</span>
                            </div>
                            <div style={{...styles.infoRow, borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 12, marginTop: 4}}>
                                <span style={styles.infoLabel}>You'll receive:</span>
                                <span style={styles.infoValueGold}>{lilapeAmount.toLocaleString()} LILAPE</span>
                            </div>
                        </div>

                        <button
                            onClick={handlePurchase}
                            disabled={!connected || isProcessing || parseFloat(tonAmount) < MIN_PURCHASE}
                            style={styles.buyBtn}
                        >
                            {!connected ? '🔗 Connect Wallet First' :
                             isProcessing ? 'Processing...' :
                             `💎 Buy for ${tonAmount} TON`}
                        </button>
                    </>
                )}

                {purchaseStep === 'sending' && (
                    <div style={styles.centerBox}>
                        <div style={styles.spinner}></div>
                        <h3 style={styles.statusTitle}>Sending Transaction</h3>
                        <p style={styles.statusText}>Confirm in your wallet</p>
                    </div>
                )}

                {purchaseStep === 'success' && (
                    <div style={styles.centerBox}>
                        <div style={styles.successIcon}>✅</div>
                        <h3 style={styles.statusTitle}>Purchase Successful!</h3>
                        <p style={styles.successAmount}>+{lilapeAmount.toLocaleString()} LILAPE</p>
                        <p style={styles.statusText}>Tokens sent to your wallet</p>
                        <button onClick={handleClose} style={{...styles.btnSuccess, marginTop: 20}}>Done</button>
                    </div>
                )}

                {purchaseStep === 'error' && (
                    <div style={styles.centerBox}>
                        <div style={styles.errorIcon}>❌</div>
                        <h3 style={styles.statusTitle}>Error</h3>
                        <p style={styles.errorText}>{errorMessage}</p>
                        <div style={styles.btnRow}>
                            <button onClick={resetModal} style={styles.btnSecondary}>Try Again</button>
                            <button onClick={handleClose} style={styles.btnError}>Close</button>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};
